import { select, put, call } from 'redux-saga/effects';
import { requestContentSliceSucceeded, requestContentSliceSucceededWithMetadata } from '../actions/RequestContentSliceSucceededAction';
import {
  selectSingleContentTypeSearchResult,
  selectSearchTerm,
  selectFilterQuery,
  selectSortSelection,
} from '../selectors/selectSingleContentTypeSearchResult';
import { RequestContentSliceAction } from '../actions/ActionTypes';
import { SearchResultItem, SearchResultList } from '../models/SearchResultTypes';
import { FilterQuery } from '../state/FilterState';
import { ContentService } from '../services/ContentService';
import { getSortSelectionFromUrl, getValidSortClause } from '../utils/SortUtils';
import { SortSelection } from '../models/SortSelection';
import { orderBy } from '../actions/OrderByAction';
import { InitialContentSliceMaxSize } from '../components/SingleContentTypeResultPage';

// This saga simulates grabbing more data for the card scroller should be changed once backend is defined
export function* requestContentSlice(action: RequestContentSliceAction) {
  const contentType = action.contentType;
  const searchTerm: string = yield select(selectSearchTerm);
  const filters: FilterQuery = yield select(selectFilterQuery);
  const currentResults: SearchResultList<SearchResultItem> = yield select(selectSingleContentTypeSearchResult);

  let sortSelection: SortSelection | undefined = yield select(selectSortSelection); //try to get sort from the state
  if (!sortSelection) {
    sortSelection = getSortSelectionFromUrl(contentType); // try to get it from the url
    if (sortSelection) {
      //if the url did have sort info update the state accordingly
      yield put(orderBy(sortSelection));
    }
  }
  const sort = getValidSortClause(contentType, sortSelection);

  if (!currentResults) {
    const results: SearchResultList<SearchResultItem> = yield call(
      ContentService.instance.fetchContent,
      contentType,
      searchTerm,
      0,
      InitialContentSliceMaxSize,
      filters,
      sort
    );
    const addEmptyList = new Array(results.totalCount - results.list.length);
    const list = results.list.concat(addEmptyList);
    yield put(requestContentSliceSucceeded({ type: results.type, list: list, totalCount: results.totalCount, facets: results.facets }));

    const resultsWithMetadata: { data: SearchResultItem[] } = yield call(
      ContentService.instance.fetchContentMetadata,
      contentType,
      results.list
    );
    if (resultsWithMetadata) {
      yield put(requestContentSliceSucceededWithMetadata(contentType, resultsWithMetadata.data));
    }
  } else {
    const startIndexToFetch = action.offset;
    const endIndexToFetch = action.count + action.offset;

    const newList = currentResults.list.slice();

    if (!allItemsCRASSDataExistInRange(newList, startIndexToFetch, endIndexToFetch, currentResults.totalCount)) {
      const slicedResults: SearchResultList<SearchResultItem> = yield call(
        ContentService.instance.fetchContent,
        contentType,
        searchTerm,
        action.offset,
        action.count,
        filters,
        sort
      );

      for (let i = startIndexToFetch; i < endIndexToFetch && i < currentResults.totalCount; i++) {
        if (newList[i] === null || newList[i] === undefined) {
          newList[i] = slicedResults.list[i - startIndexToFetch];
        }
      }
      const singleContentType = {
        type: currentResults.type,
        list: newList,
        totalCount: currentResults.totalCount,
        facets: currentResults.facets,
      };
      yield put(requestContentSliceSucceeded(singleContentType));
    }
    if (!allItemsMetaDataExistInRange(newList, startIndexToFetch, endIndexToFetch, currentResults.totalCount)) {
      const resultsForMetaData: SearchResultList<SearchResultItem> = yield select(selectSingleContentTypeSearchResult);
      const resultsWithMetadata: { data: SearchResultItem[] } = yield call(
        ContentService.instance.fetchContentMetadata,
        contentType,
        resultsForMetaData.list.slice(startIndexToFetch, endIndexToFetch)
      );
      if (resultsWithMetadata) {
        yield put(requestContentSliceSucceededWithMetadata(contentType, resultsWithMetadata.data));
      }
    }
  }
}

export function allItemsCRASSDataExistInRange(items: SearchResultItem[], startItemIndex: number, endItemIndex: number, total: number) {
  for (let i = startItemIndex; i < endItemIndex && i < total; i++) {
    if (!items[i] || items[i] === null || items[i] === undefined || !items[i].crassDataLoaded) {
      return false;
    }
  }
  return true;
}

export function allItemsMetaDataExistInRange(items: SearchResultItem[], startItemIndex: number, endItemIndex: number, total: number) {
  for (let i = startItemIndex; i < endItemIndex && i < total; i++) {
    if (!items[i] || items[i] === null || items[i] === undefined || !items[i].metaDataLoaded) {
      return false;
    }
  }
  return true;
}
